import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI, api } from '../services/api';
import { setAuth, setLastTenant, getLastTenant } from '../services/auth';
import { validatePassword, PASSWORD_HINT } from '../utils/passwordValidation';
import './Login.css';

function Login() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState('password'); // 'password' or 'tenant'
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [availableTenants, setAvailableTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [setPasswordData, setSetPasswordData] = useState({ username: '', password: '', confirmPassword: '' });
  const [setPasswordLoading, setSetPasswordLoading] = useState(false);
  const [setPasswordError, setSetPasswordError] = useState('');
  const [setPasswordSuccess, setSetPasswordSuccess] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [microsoftAuthTenants, setMicrosoftAuthTenants] = useState([]);
  const [microsoftSelectedTenant, setMicrosoftSelectedTenant] = useState('');
  const [microsoftDiscoverEnabled, setMicrosoftDiscoverEnabled] = useState(false);
  const [authConfigLoading, setAuthConfigLoading] = useState(true);
  const [choiceCode, setChoiceCode] = useState('');
  const [tenantChoiceList, setTenantChoiceList] = useState([]);
  const [selectedChoiceTenant, setSelectedChoiceTenant] = useState('');
  const [choiceLoading, setChoiceLoading] = useState(false);

  // Handle Microsoft callback: URL has ?token=...&tenant_code=... (and optionally microsoft=1)
  useEffect(() => {
    const token = searchParams.get('token');
    const tenantCode = searchParams.get('tenant_code');
    const err = searchParams.get('error');
    if (err) {
      setError(decodeURIComponent(err));
      setSearchParams({}, { replace: true });
      return;
    }
    if (!token || !tenantCode) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setAuthConfigLoading(false);
      try {
        setAuth(token);
        const whoamiRes = await api.get('/whoami', {
          headers: { 'x-tenant-code': tenantCode },
        });
        if (cancelled) return;
        if (whoamiRes.data?.success && whoamiRes.data.tenant && whoamiRes.data.user) {
          setAuth(token, {
            user: whoamiRes.data.user,
            tenant: whoamiRes.data.tenant,
            membership: whoamiRes.data.membership,
            memberships: whoamiRes.data.memberships || [],
            permissions: whoamiRes.data.permissions || [],
            permissionDetails: whoamiRes.data.permissionDetails || [],
          });
          setLastTenant(tenantCode);
          setSearchParams({}, { replace: true });
          navigate(`/${tenantCode}/dashboard`, { replace: true });
          window.location.reload();
        } else {
          setError('Could not load your session. Please try logging in again.');
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.error || e.message || 'Session could not be loaded.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams, setSearchParams, navigate]);

  // Fetch auth config (Microsoft tenants + discover flag) on mount
  useEffect(() => {
    if (!authConfigLoading && microsoftAuthTenants.length > 0 && !microsoftDiscoverEnabled) return;
    authAPI.getConfig()
      .then((res) => {
        const data = res.data?.data || {};
        const list = data.microsoftAuthTenants;
        if (list?.length) {
          setMicrosoftAuthTenants(list);
          const last = getLastTenant();
          const match = list.find((t) => t.code === last);
          setMicrosoftSelectedTenant(match ? last : list[0].code);
        }
        if (data.microsoftDiscoverEnabled) setMicrosoftDiscoverEnabled(true);
        // When Microsoft button would be hidden, log what backend returned (helps debug production)
        if (!list?.length && !data.microsoftDiscoverEnabled) {
          console.warn('[Login] auth config has no Microsoft option', data);
        }
      })
      .catch((err) => {
        console.warn('[Login] auth config request failed', err?.message || err, err?.response?.status, err?.response?.data);
      })
      .finally(() => setAuthConfigLoading(false));
  }, [authConfigLoading, microsoftAuthTenants.length, microsoftDiscoverEnabled]);

  // Handle Microsoft discover flow: choice_code in URL → fetch tenant list, show picker
  useEffect(() => {
    const code = searchParams.get('choice_code');
    if (!code) return;
    setChoiceCode(code);
    setError('');
    authAPI.getTenantChoice(code)
      .then((res) => {
        const tenants = res.data?.data?.tenants || [];
        setTenantChoiceList(tenants);
        if (tenants.length > 0) setSelectedChoiceTenant(tenants[0].code);
      })
      .catch((e) => setError(e.response?.data?.error || 'Failed to load options. Please sign in again.'));
  }, [searchParams]);

  // Load last tenant on mount
  useEffect(() => {
    if (step === 'tenant') {
      const lastTenant = getLastTenant();
      if (lastTenant) {
        setSelectedTenant(lastTenant);
      }
    }
  }, [step]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleCompleteTenantChoice = async (e) => {
    e.preventDefault();
    if (!choiceCode || !selectedChoiceTenant) return;
    setChoiceLoading(true);
    setError('');
    try {
      const res = await authAPI.postMicrosoftComplete(choiceCode, selectedChoiceTenant);
      const token = res.data?.data?.token;
      const tenantCode = res.data?.data?.tenant_code;
      if (!token || !tenantCode) {
        setError('Sign-in could not be completed.');
        return;
      }
      setAuth(token);
      const whoamiRes = await api.get('/whoami', { headers: { 'x-tenant-code': tenantCode } });
      if (whoamiRes.data?.success && whoamiRes.data.tenant && whoamiRes.data.user) {
        setAuth(token, {
          user: whoamiRes.data.user,
          tenant: whoamiRes.data.tenant,
          membership: whoamiRes.data.membership,
          memberships: whoamiRes.data.memberships || [],
          permissions: whoamiRes.data.permissions || [],
          permissionDetails: whoamiRes.data.permissionDetails || [],
        });
        setLastTenant(tenantCode);
        setSearchParams({}, { replace: true });
        navigate(`/${tenantCode}/dashboard`, { replace: true });
        window.location.reload();
      } else {
        setError('Could not load your session.');
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Sign-in failed.');
    } finally {
      setChoiceLoading(false);
    }
  };

  const handleSetPasswordChange = (e) => {
    const { name, value } = e.target;
    setSetPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    setSetPasswordError('');
    setSetPasswordSuccess('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Step 1: Validate password (without tenant_code)
      const response = await authAPI.login({
        username: formData.username,
        password: formData.password
        // No tenant_code - this will return available tenants
      });
      
      if (response.data.success) {
        // Check if this is the tenant selection response (new two-step flow)
        if (response.data.requiresTenantSelection || response.data.data?.availableTenants) {
          // User has multiple tenants or no tenant specified - show selector
          const tenants = response.data.data?.availableTenants || [];
          
          if (tenants.length === 0) {
            setError('You do not have access to any tenants. Please contact your administrator.');
            setLoading(false);
            return;
          }

          if (tenants.length === 1) {
            // Only one tenant - auto-select it
            handleTenantSelect(tenants[0].code);
            return;
          }

          // Multiple tenants - show selector
          setAvailableTenants(tenants);
          setStep('tenant');
          setLoading(false);
        } else if (response.data.data?.token) {
          // This is a complete login response (old format or tenant was auto-selected)
          // This can happen if backend still uses old format or user has only one tenant
          setAuth(response.data.data.token, {
            user: response.data.data.user,
            tenant: response.data.data.tenant,
            membership: response.data.data.membership,
            memberships: response.data.data.memberships || [],
            permissions: response.data.data.permissions || [],
            permissionDetails: response.data.data.permissionDetails || [],
          });
          if (response.data.data.tenant?.code) {
            setLastTenant(response.data.data.tenant.code);
          }
          const code = response.data.data.tenant?.code;
          navigate(code ? `/${code}/dashboard` : '/', { replace: true });
          window.location.reload();
        } else {
          // Unexpected response structure - log for debugging
          console.error('Unexpected login response structure:', {
            success: response.data.success,
            hasToken: !!response.data.data?.token,
            hasAvailableTenants: !!response.data.data?.availableTenants,
            requiresTenantSelection: response.data.requiresTenantSelection,
            fullResponse: response.data
          });
          setError('Unexpected response from server. The backend may need to be restarted. Please contact support.');
          setLoading(false);
        }
      } else {
        setError(response.data.error || 'Login failed');
        setLoading(false);
      }
    } catch (err) {
      console.error('Login error:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        config: err.config
      });
      const errorMessage = err.response?.data?.error || err.message || 'Network Error - Check if backend is running';
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleTenantSelect = async (tenantCode) => {
    if (!tenantCode) {
      setError('Please select a tenant');
      return;
    }

    setError('');
    setLoading(true);

    try {
      // Step 2: Complete login with tenant
      const response = await authAPI.login({
        username: formData.username,
        password: formData.password,
        tenant_code: tenantCode
      });
      
      if (response.data.success) {
        // Store auth and last tenant
        setAuth(response.data.data.token, {
          user: response.data.data.user,
          tenant: response.data.data.tenant,
          membership: response.data.data.membership,
          memberships: response.data.data.memberships || [],
          permissions: response.data.data.permissions || [],
          permissionDetails: response.data.data.permissionDetails || [],
        });
        setLastTenant(tenantCode);

        // Redirect to tenant home (tenant in URL for shareable links)
        navigate(`/${tenantCode}/dashboard`, { replace: true });
        window.location.reload();
      } else {
        setError(response.data.error || 'Login failed');
        setLoading(false);
      }
    } catch (err) {
      console.error('Tenant selection error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Login failed';
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleTenantSubmit = async (e) => {
    e.preventDefault();
    await handleTenantSelect(selectedTenant);
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setSetPasswordError('');
    setSetPasswordSuccess('');

    if (setPasswordData.password !== setPasswordData.confirmPassword) {
      setSetPasswordError('Passwords do not match');
      return;
    }

    const pwdError = validatePassword(setPasswordData.password);
    if (pwdError) {
      setSetPasswordError(pwdError);
      return;
    }

    setSetPasswordLoading(true);

    try {
      const response = await authAPI.setInitialPassword(setPasswordData.username, setPasswordData.password);
      
      if (response.data.success) {
        setSetPasswordSuccess(response.data.message);
        setSetPasswordData({ username: '', password: '', confirmPassword: '' });
        setTimeout(() => {
          setShowSetPassword(false);
        }, 2000);
      } else {
        setSetPasswordError(response.data.error || 'Failed to set password');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to set password';
      setSetPasswordError(errorMessage);
    } finally {
      setSetPasswordLoading(false);
    }
  };

  const handleBackToPassword = () => {
    setStep('password');
    setError('');
    setAvailableTenants([]);
    setSelectedTenant('');
  };

  if (showSetPassword) {
    return (
      <div className="login-container">
        <div className="login-card">
          <h1>Child Family Connect</h1>
          <h2>Set Initial Password</h2>
          
          {setPasswordError && (
            <div className="login-error">
              {setPasswordError}
            </div>
          )}

          {setPasswordSuccess && (
            <div className="login-success">
              {setPasswordSuccess}
            </div>
          )}

          <form onSubmit={handleSetPassword}>
            <div className="form-group">
              <label htmlFor="set-username">Username or Email</label>
              <input
                type="text"
                id="set-username"
                name="username"
                value={setPasswordData.username}
                onChange={handleSetPasswordChange}
                required
                autoFocus
                disabled={setPasswordLoading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="set-password">Password</label>
              <input
                type="password"
                id="set-password"
                name="password"
                value={setPasswordData.password}
                onChange={handleSetPasswordChange}
                required
                disabled={setPasswordLoading}
              />
              <small>{PASSWORD_HINT}</small>
            </div>

            <div className="form-group">
              <label htmlFor="confirm-password">Confirm Password</label>
              <input
                type="password"
                id="confirm-password"
                name="confirmPassword"
                value={setPasswordData.confirmPassword}
                onChange={handleSetPasswordChange}
                required
                disabled={setPasswordLoading}
              />
            </div>

            <button 
              type="submit" 
              className="login-button"
              disabled={setPasswordLoading}
            >
              {setPasswordLoading ? 'Setting password...' : 'Set Password'}
            </button>
          </form>

          <div className="login-info">
            <p>
              <button 
                type="button"
                className="link-button"
                onClick={() => {
                  setShowSetPassword(false);
                  setSetPasswordError('');
                  setSetPasswordSuccess('');
                }}
                style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
              >
                Back to login
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Tenant choice step (discover flow: user is in multiple CFC tenants)
  if (choiceCode && tenantChoiceList.length > 0) {
    return (
      <div className="login-container">
        <div className="login-card">
          <h1>Child Family Connect</h1>
          <h2>Choose tenant</h2>
          <p className="login-choice-intro">You have access to more than one tenant. Choose one to continue.</p>
          {error && <div className="login-error">{error}</div>}
          <form onSubmit={handleCompleteTenantChoice}>
            <div className="form-group">
              <label htmlFor="choice-tenant">Tenant</label>
              <select
                id="choice-tenant"
                value={selectedChoiceTenant}
                onChange={(e) => setSelectedChoiceTenant(e.target.value)}
                disabled={choiceLoading}
                className="form-select"
              >
                {tenantChoiceList.map((t) => (
                  <option key={t.code} value={t.code}>{t.name} ({t.code})</option>
                ))}
              </select>
            </div>
            <button type="submit" className="login-button" disabled={choiceLoading}>
              {choiceLoading ? 'Opening...' : 'Continue'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <h1>Child Family Connect</h1>
        <h2>{step === 'tenant' ? 'Select Tenant' : 'Login'}</h2>
        
        {error && (
          <div className="login-error">
            {error}
          </div>
        )}

        {step === 'password' ? (
          <form onSubmit={handlePasswordSubmit}>
            <div className="form-group">
              <label htmlFor="username">Username or Email</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                autoFocus
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <button 
              type="submit" 
              className="login-button"
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Continue'}
            </button>

            {(microsoftAuthTenants.length > 0 || microsoftDiscoverEnabled) && (
              <>
                <div className="login-divider">or</div>
                <div className="login-microsoft">
                  {!microsoftDiscoverEnabled && microsoftAuthTenants.length > 1 && (
                    <>
                      <label htmlFor="microsoft-tenant">Your organization</label>
                      <select
                        id="microsoft-tenant"
                        value={microsoftSelectedTenant}
                        onChange={(e) => setMicrosoftSelectedTenant(e.target.value)}
                        disabled={loading}
                        className="form-select"
                      >
                        {microsoftAuthTenants.map((t) => (
                          <option key={t.code} value={t.code}>{t.name}</option>
                        ))}
                      </select>
                    </>
                  )}
                  <button
                    type="button"
                    className="login-button login-button-microsoft"
                    disabled={loading}
                    onClick={() => {
                      if (microsoftDiscoverEnabled) {
                        window.location.href = authAPI.getMicrosoftRedirectUrlDiscover();
                      } else {
                        const tenantCode = microsoftAuthTenants.length === 1
                          ? microsoftAuthTenants[0].code
                          : microsoftSelectedTenant;
                        window.location.href = authAPI.getMicrosoftRedirectUrl(tenantCode);
                      }
                    }}
                  >
                    Sign in with Microsoft
                  </button>
                </div>
              </>
            )}
          </form>
        ) : (
          <form onSubmit={handleTenantSubmit}>
            <div className="form-group">
              <label htmlFor="tenant-select">Select Tenant</label>
              <select
                id="tenant-select"
                value={selectedTenant}
                onChange={(e) => setSelectedTenant(e.target.value)}
                required
                disabled={loading}
                className="form-select"
              >
                <option value="">-- Select a tenant --</option>
                {availableTenants.map(tenant => (
                  <option key={tenant.id} value={tenant.code}>
                    {tenant.name} ({tenant.code}) - {tenant.role_name}
                  </option>
                ))}
              </select>
              <small>Select the tenant you want to access</small>
            </div>

            <div className="form-group" style={{ display: 'flex', gap: '10px' }}>
              <button 
                type="button"
                className="login-button"
                onClick={handleBackToPassword}
                disabled={loading}
                style={{ flex: 1, backgroundColor: '#6c757d' }}
              >
                Back
              </button>
              <button 
                type="submit" 
                className="login-button"
                disabled={loading || !selectedTenant}
                style={{ flex: 1 }}
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </div>
          </form>
        )}

        {step === 'password' && (
          <div className="login-info">
            <div className="login-info-links">
              <button
                type="button"
                className="login-link-button"
                onClick={() => setShowSetPassword(true)}
              >
                Don't have a password? Set your initial password here.
              </button>
              <button
                type="button"
                className="login-link-button"
                onClick={() => setShowForgotPassword(true)}
              >
                Forgot password?
              </button>
            </div>
            {showForgotPassword && (
              <p className="login-forgot-message">
                Contact your administrator to reset your password.
                <button type="button" className="login-link-button" onClick={() => setShowForgotPassword(false)}>Dismiss</button>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Login;
